import Icon from '../ui/Icon';
import { copyText } from '../../utils/clipboard';
import { chipState, describeTime, formatPrice, hubBadge, stationInitials, unitLabel } from './stationAdapter';

const TH = 'py-3 px-4 text-left text-[11px] leading-[14px] font-semibold text-on-surface-variant uppercase tracking-wider';

const CHIP_STYLES = {
  latest: 'bg-primary text-on-primary font-bold',
  priced: 'bg-surface-container text-on-surface',
  noPrice: 'bg-surface-container-low text-on-surface-variant border border-dashed border-outline-variant',
  notSold: 'bg-surface-container-low text-on-surface-variant line-through',
};

function FuelChip({ fuel, state, onClick }) {
  const hint =
    state === 'notSold'
      ? 'Marked as not sold'
      : state === 'noPrice'
        ? `${fuel.name}: no price yet`
        : `${fuel.name}: ${formatPrice(fuel.latestPrice)} ${unitLabel(fuel.unit)}`;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${hint}. Click to edit.`}
      aria-label={`Edit ${fuel.name} (${state === 'notSold' ? 'marked as not sold' : state === 'noPrice' ? 'no price' : formatPrice(fuel.latestPrice)})`}
      className={`px-2 py-0.5 rounded text-[11px] whitespace-nowrap transition-colors hover:ring-1 hover:ring-primary-container outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot ${CHIP_STYLES[state]}`}
    >
      {fuel.name}
      {state === 'noPrice' && <span className="ml-1 text-[10px] italic no-underline">· no price</span>}
    </button>
  );
}

function StationCell({ station }) {
  const initials = stationInitials(station.name);
  const invalid = station.googleStatus === 'not_found';
  return (
    <div className="flex items-start gap-3 min-w-[260px]">
      <div
        className="w-10 h-10 rounded-lg bg-surface-container-low flex items-center justify-center shrink-0 text-sm font-bold text-amber-700"
        aria-hidden="true"
      >
        {initials ?? <Icon name="local_gas_station" size={20} className="text-on-surface-variant" />}
      </div>
      <div className="flex flex-col min-w-0">
        {station.name ? (
          <span className="text-sm font-semibold text-on-surface truncate">{station.name}</span>
        ) : (
          <span className="text-sm font-semibold text-on-surface-variant italic">Name unavailable</span>
        )}
        {station.address && <span className="text-xs text-on-surface-variant truncate">{station.address}</span>}
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          <span className="font-data-mono text-[10px] text-on-surface-variant bg-surface-container px-1 rounded break-all" title="Google Place ID">
            {station.placeId}
          </span>
          <button
            type="button"
            onClick={() => copyText(station.placeId, 'Place ID copied')}
            aria-label="Copy Place ID"
            title="Copy Place ID"
            className="text-on-surface-variant hover:text-primary rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
          >
            <Icon name="content_copy" size={13} />
          </button>
          {invalid && (
            <span className="inline-flex items-center gap-1 px-1.5 rounded-full border border-status-alert-border bg-status-alert-bg text-status-alert-text text-[10px] font-semibold">
              <Icon name="warning" size={11} />
              Invalid place ID
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function SkeletonRows({ count }) {
  return Array.from({ length: count }, (_, i) => (
    <tr key={i} className="animate-pulse" aria-hidden="true">
      <td className="py-4 px-4">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-lg bg-surface-container" />
          <div className="space-y-1.5">
            <div className="h-3 w-44 bg-surface-container rounded" />
            <div className="h-2.5 w-32 bg-surface-container-low rounded" />
          </div>
        </div>
      </td>
      {[96, 180, 70, 50].map((w, j) => (
        <td key={j} className="py-4 px-4">
          <div className="h-3 bg-surface-container-low rounded" style={{ width: w }} />
        </td>
      ))}
    </tr>
  ));
}

/** Stations with their hub type, configured fuels (clickable chips) and latest price. */
export default function StationsTable({ stations, loading, refreshing, skeletonCount = 5, onEdit, onEditFuel }) {
  return (
    <div className="w-full overflow-x-auto" aria-busy={loading || refreshing}>
      <table className="w-full min-w-[960px] text-left border-collapse">
        <caption className="sr-only">Gas stations and charging points</caption>
        <thead>
          <tr className="bg-surface-container-low border-b border-surface-container">
            <th scope="col" className={TH}>
              Station &amp; Place ID
            </th>
            <th scope="col" className={TH}>
              Hub Classification
            </th>
            <th scope="col" className={TH}>
              Configured Fuel &amp; Charging
            </th>
            <th scope="col" className={TH}>
              Latest Price
            </th>
            <th scope="col" className={`${TH} text-right`}>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className={`divide-y divide-surface-container transition-opacity ${refreshing ? 'opacity-50' : ''}`}>
          {loading ? (
            <SkeletonRows count={skeletonCount} />
          ) : (
            stations.map((station) => {
              const badge = hubBadge(station);
              const latest = station.latestPrice;
              const time = describeTime(latest?.updatedAt);
              return (
                <tr key={station.placeId} className="hover:bg-surface-container-low/50 transition-colors align-top">
                  <th scope="row" className="py-3.5 px-4 font-normal text-left">
                    <StationCell station={station} />
                  </th>
                  <td className="py-3.5 px-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surface-container-high text-on-surface text-[11px] font-semibold whitespace-nowrap">
                      <Icon name={badge.icon} size={14} className={badge.iconClass} />
                      {badge.label}
                    </span>
                  </td>
                  <td className="py-3.5 px-4">
                    <div className="flex flex-wrap gap-1 max-w-[300px]">
                      {station.fuels.length === 0 ? (
                        <span className="text-xs text-on-surface-variant">—</span>
                      ) : (
                        station.fuels.map((fuel) => (
                          <FuelChip
                            key={fuel.fuelOptionId}
                            fuel={fuel}
                            state={chipState(fuel, latest?.fuelOptionId)}
                            onClick={() => onEditFuel(station, fuel.fuelOptionId)}
                          />
                        ))
                      )}
                    </div>
                  </td>
                  <td className="py-3.5 px-4 whitespace-nowrap">
                    {latest ? (
                      <div className="flex flex-col" title={`${latest.name} · updated ${time.relative}${time.exact ? ` (${time.exact})` : ''}`}>
                        <span className="text-base font-bold text-on-surface font-data-mono">{formatPrice(latest.price)}</span>
                        <span className="text-[11px] text-on-surface-variant">{unitLabel(latest.unit)}</span>
                      </div>
                    ) : (
                      <span className="text-on-surface-variant">—</span>
                    )}
                  </td>
                  <td className="py-3.5 px-4 text-right">
                    <button
                      type="button"
                      onClick={() => onEdit(station)}
                      aria-label={`Edit prices for ${station.name ?? station.placeId}`}
                      className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-semibold text-primary hover:bg-surface-container outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
                    >
                      <Icon name="edit" size={16} />
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
